import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import React, { FC, useState } from 'react'
import { Trans, t, Plural } from '@lingui/macro'

import {
  EventModelFilterInput,
  EventModelFilterEnum,
  OrderDescriptor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { Page } from '../../../components/common/page/Page'
import PageHeader from '../../../components/common/page/header/PageHeader'

import { useListEventsAsAdminQuery } from '../operations/listEventsAsAdmin.codegen'

import { isIE } from 'shared/helpers'
import { Redirect } from 'react-router'
import {
  DEFAULT_PAGE_LIMIT,
  TableExtended
} from 'front-end/src/components/common/table-extended/TableExtended'
import DateIcon from '../components/date-icon/DateIcon'
import dayjs from 'shared/dayjs'
import {
  Box,
  ButtonIcon,
  Flex,
  Link,
  Select,
  SimpleGrid,
  Tooltip,
  Text
} from 'front-end/src/components'
import { brandColors } from 'shared/basicPalette'
import IconLock from 'front-end/src/components/common/icon/IconLock'
import IconLockOpen from 'front-end/src/components/common/icon/IconLockOpen'
import Tags from 'front-end/src/components/common/tag/Tags'
import { ITag } from 'types/Tag'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { apolloClientPrivate } from 'front-end/src/graphql/apolloClient'
import { REMOVE_TAG_MUTATION } from '../operations/removeTagMutation'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import EventStatus from '../components/event-status/EventStatus'
import EventRSVP from './components/EventRSVP'
import ResettableInput from 'front-end/src/components/common/form/input/ResettableInput'
import { chain } from 'lodash'
import { CreateEventButton } from './components/CreateEventButton'
import { useListTagsQuery } from '../operations/listTags.codegen'
import { ReactSelectOption } from 'front-end/src/components/common/select/Select'
import { SQLOrderDirectionEnum } from 'front-end/../shared/enums/common'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import IconDuplicate from 'front-end/src/components/common/icon/IconDuplicate'
import { useCopyEventMutation } from './ManageEventsPage.codegen'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { CopyConfirmModal } from './CopyConfirmModal'
import { css } from '@emotion/react'
import EventFormattedTime from 'front-end/../shared/components/event-formatted-time/EventFormattedTime'
import { useEventsTimezoneQuery } from '../components/event-card/EventCard.codegen'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import { SyncAlert } from '../components/sync-alert/SyncAlert'

export enum EventPeriodEnum {
  PAST = 'past',
  FUTURE = 'future'
}

export const anyOption = {
  value: null as any,
  label: <Trans>Any</Trans>
}

export const CalendarEventPeriodOptions: ReactSelectOption[] = [
  {
    value: EventPeriodEnum.FUTURE,
    label: <Trans>Upcoming events</Trans>
  },
  {
    value: EventPeriodEnum.PAST,
    label: <Trans>Past events</Trans>
  }
]

const CalendarEventStatusOptions = [
  anyOption,
  { value: 'draft', label: <Trans>Draft</Trans> },
  { value: 'open', label: <Trans>Open</Trans> },
  { value: 'inProgress', label: <Trans>In progress</Trans> },
  { value: 'completed', label: <Trans>Closed</Trans> }
]

export const ManageEventsPage: FC = () => {
  if (isIE) {
    // manage events cannot be edited in IE. It cannot parse the date strings correctly without intl-datetimeformat/polyfill
    return <Redirect to={staticLinks.events} />
  }
  const [state, setState] = useState({
    filter: {
      period: CalendarEventPeriodOptions[0],
      status: anyOption,
      tag: anyOption,
      search: ''
    },
    page: 1,
    pageSize: DEFAULT_PAGE_LIMIT,
    order: {
      name: 'start_time',
      sort: SQLOrderDirectionEnum.ASC
    }
  })

  const { open: openCopyConfirm, currentModal: copyConfirmModal } = useModal()

  const {
    filter: { period, status, tag, search }
  } = state

  const filters: EventModelFilterInput[] = []

  if (search) filters.push({ name: EventModelFilterEnum.search, value: search })
  if (status.value)
    filters.push({ name: EventModelFilterEnum.status, value: status.value })
  if (tag.value)
    filters.push({ name: EventModelFilterEnum.tagId, value: tag.value })
  if (period.value)
    filters.push({ name: EventModelFilterEnum.period, value: period.value })

  const { data, refetch, loading } = useListEventsAsAdminQuery({
    variables: {
      filters,
      limit: state.pageSize,
      offset: (state.page - 1) * DEFAULT_PAGE_LIMIT,
      order: state.order
    }
  })
  const { data: tagsData } = useListTagsQuery()
  const { filter } = state

  const [copyEvent, { loading: loadingCopyEvent }] = useCopyEventMutation({
    onCompleted: (data) => {
      data.admin.event.duplicate.id &&
        navigateTo(linkBuilder.manageEvent(data.admin.event.duplicate.id))
      refetch()
    },
    onError: (e) => {
      notification.error(e.response.data.message)
    }
  })

  const showCopyConfirmModal = async ({ eventId }) => {
    openCopyConfirm(CopyConfirmModal)
      .then(async () => {
        copyEvent({
          variables: {
            eventId: eventId
          }
        })
      })
      .catch(() => notification.error(t`Copy event has failed. Try again.`))
  }

  const getTagOptions = () => {
    return chain(tagsData?.tags ?? [])
      .orderBy(({ name }) => name)
      .map(({ id, name }) => ({ value: String(id), label: name }))
      .unshift(anyOption as any)
      .value()
  }

  const changeState = (changes) => {
    setState({
      ...state,
      ...changes
    })
  }

  const onFilterChange = (filter) => {
    const { filter: oldFilter } = state
    changeState({
      page: 1,
      filter: { ...oldFilter, ...filter }
    })
  }

  const onPageChange = (page: number) => {
    changeState({
      page
    })
  }

  const onOrderChange = (order: OrderDescriptor) => {
    changeState({
      order: order as { name: string; sort: SQLOrderDirectionEnum }
    })
  }

  const onPeriodChange = (period) => {
    const { filter: oldFilter } = state
    changeState({
      page: 1,
      filter: { ...oldFilter, period },
      order: {
        name: 'start_time',
        sort:
          period.value === EventPeriodEnum.PAST
            ? SQLOrderDirectionEnum.DESC
            : SQLOrderDirectionEnum.ASC
      }
    })
  }

  const { data: eventsTimezone } = useEventsTimezoneQuery()

  const columns = React.useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'start_time',
        width: '72px',
        align: 'center',
        Cell: ({ row }) => (
          <Tooltip text={dayjs(row.original.start_time).format('L')}>
            <DateIcon
              date={dayjs(row.original.start_time)}
              dataTest="EventDate"
            />
          </Tooltip>
        )
      },
      {
        Header: 'Name',
        accessor: 'title',
        Cell: ({ row }) => (
          <>
            <Flex direction="column">
              <Box fontSize="18px" fontWeight="500" data-test="EventTitle">
                {row.original.title}
              </Box>
              <Box mt="4px" fontSize="12px" color={brandColors.grey[400]}>
                {row.original.is_open ? (
                  <>
                    <IconLockOpen mr="6px" />
                    Public
                  </>
                ) : (
                  <>
                    <IconLock mr="6px" />
                    Private
                  </>
                )}
                <IconClock mr="8px" ml="16px" pt="3px" />
                <EventFormattedTime
                  event={row.original}
                  timezone={eventsTimezone?.me.timezone}
                />
              </Box>
              {row.original.recurring_event_id && (
                <Flex fontSize="12px">
                  <Link
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    css={css`
                      // TODO: Replace this with future mobile implementation in TableExtended
                      &:hover {
                        text-decoration: none;
                        .chakra-text {
                          text-decoration: underline;
                        }
                      }
                    `}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      navigateTo(
                        linkBuilder.manageEvent(
                          row.original.recurring_event_id,
                          true,
                          'occurrences'
                        )
                      )
                    }}
                  >
                    <IconRefresh mr="6px" />
                    <Text>
                      <RecurrenceText
                        recurrence={row.original.recurringEvent.recurrence}
                      />
                    </Text>
                  </Link>
                </Flex>
              )}
              <Flex mt="4px">
                <Tags
                  items={row.original.tags}
                  onRemove={async ({ id: tagId }: ITag) => {
                    await apolloClientPrivate.mutate({
                      mutation: REMOVE_TAG_MUTATION,
                      variables: { eventId: row.original.id, tagId }
                    })

                    refetch()
                  }}
                  onClick={(eventTag) => {
                    navigateTo(linkBuilder.eventTag(eventTag.id))
                  }}
                />
              </Flex>
            </Flex>
          </>
        )
      },
      {
        Header: 'Status',
        disableSortBy: true,
        accessor: 'status',
        width: '110px',
        Cell: ({ row }) => (
          <EventStatus alignItems="center" event={row.original} />
        )
      },
      {
        Header: 'RSVP',
        disableSortBy: true,
        accessor: 'inviteesCount',
        width: '160px',
        align: 'center',
        Cell: ({ row }) => (
          <EventRSVP alignItems="center" event={row.original} />
        )
      },
      {
        Header: '',
        disableSortBy: true,
        accessor: 'actions',
        width: '48px',
        align: 'right',
        Cell: ({ row }) => (
          <>
            {!row.original.recurringEvent && (
              <Flex alignItems="center" className="hide-and-show-on-hover">
                <ButtonIcon
                  className="EventDetail__duplicate"
                  hint={t`Duplicate event`}
                  aria-label={t`Duplicate event`}
                  circular
                  onClick={async (ev: React.MouseEvent) => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    await showCopyConfirmModal({
                      eventId: row.original.id
                    })
                    return false
                  }}
                  isLoading={loadingCopyEvent}
                  icon={
                    <IconDuplicate
                      color={brandColors.grey[500]}
                      boxSize="16px"
                    />
                  }
                  dataTest="CopyButton"
                />
              </Flex>
            )}
          </>
        )
      }
      /* For the phase 2
      {
        Header: '',
        disableSortBy: true,
        accessor: 'menu',
        width: '64px',
        align: 'left',
        Cell: ({ row }) => (
          <Flex alignItems="center">
            <EventActionMenu refetch={refetch} event={row.original} />
          </Flex>
        )
      } */
    ],
    []
  )

  return (
    <Page className="ManageEventsPage">
      <PageHeader
        className="PageHeader"
        title={t`Events`}
        controls={<CreateEventButton />}
      />

      <Box p="16px">
        <SyncAlert hasDismiss />
      </Box>

      <Box p="0 16px">
        <TableExtended
          loading={loading}
          columns={columns}
          data={data?.admin?.organisation?.events ?? []}
          dataCount={data?.admin?.organisation?.eventsCount ?? 0}
          page={state.page}
          onPageChange={onPageChange}
          order={state.order}
          onOrderChange={onOrderChange}
          onRowClick={(row) => {
            navigateTo(linkBuilder.manageEvent(row?.original.id))
          }}
          paginationContentBefore={
            <Box color={brandColors.grey[500]}>
              <Plural
                value={data?.admin?.organisation?.eventsCount || 0}
                _0={null}
                one={`# event`}
                other={`# events`}
              />
            </Box>
          }
          header={
            <>
              <SimpleGrid columns={4} spacing="8px">
                <Select
                  options={CalendarEventPeriodOptions}
                  onChange={(periodOption) => {
                    onPeriodChange(periodOption)
                  }}
                  label={<Trans>Show</Trans>}
                  value={filter.period}
                />
                <Select
                  options={CalendarEventStatusOptions}
                  onChange={(statusOption) =>
                    onFilterChange({
                      status: statusOption
                    })
                  }
                  label={<Trans>Status</Trans>}
                  value={status}
                />
                <Select
                  options={getTagOptions()}
                  onChange={(tagOption) => onFilterChange({ tag: tagOption })}
                  label={<Trans>Tag</Trans>}
                  value={filter.tag}
                />
                <ResettableInput
                  onChange={(search) => onFilterChange({ search })}
                  placeholder={t`Search`}
                  title={t`Search`}
                  showResetButton={!!filter.search}
                  showSearchIcon
                  value={filter.search}
                  onReset={() => onFilterChange({ search: '' })}
                />
              </SimpleGrid>
            </>
          }
        />
      </Box>
      {copyConfirmModal}
    </Page>
  )
}
